import React, { useState } from 'react';
import { ethers } from 'ethers';
import Button from '../Common/Button';
import { formatUSDT, bpsToPercent, formatTimeRemaining, parseUSDT } from '../../utils/format';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { MIN_CLAIM_USDT } from '../../config/contract';

const MIN_CLAIM_BN = ethers.utils.parseUnits(MIN_CLAIM_USDT.toString(), 18);

const ClaimWidget = ({
  pendingRewards,
  pendingROI,
  claimFee,
  dailyLimit,
  remaining,
  resetsAt,
  userId,
  onChange
}) => {
  const { contract, onCorrectNetwork, switchToCorrectNetwork } = useWeb3();
  const [busy, setBusy] = useState('');
  const [amountInput, setAmountInput] = useState('');

  const total = ethers.BigNumber.from(pendingRewards || '0')
    .add(ethers.BigNumber.from(pendingROI || '0'));

  const limitEnabled = dailyLimit && dailyLimit !== '0';
  const now = Math.floor(Date.now() / 1000);
  const secondsToReset = resetsAt > now ? resetsAt - now : 0;

  // Max claimable right now (limited by total pending + daily cap if any)
  const maxClaimable = limitEnabled
    ? (total.lt(ethers.BigNumber.from(remaining || '0')) ? total : ethers.BigNumber.from(remaining || '0'))
    : total;

  const capReached = limitEnabled && maxClaimable.lte(0) && total.gt(0);

  // What the user is actually about to claim
  let claimTarget;
  try {
    claimTarget = amountInput
      ? parseUSDT(amountInput)
      : maxClaimable;
  } catch (e) {
    claimTarget = ethers.BigNumber.from(0);
  }
  if (claimTarget.gt(maxClaimable)) claimTarget = maxClaimable;

  const feeBN = claimTarget.mul(ethers.BigNumber.from(claimFee || '1000')).div(10000);
  const payout = claimTarget.sub(feeBN);

  const fillMax = () => setAmountInput(ethers.utils.formatUnits(maxClaimable, 18));

  const updateROI = async () => {
    if (!contract || !userId) return;
    if (!onCorrectNetwork) {
      notify.warning('Switching to correct network...');
      const ok = await switchToCorrectNetwork();
      if (!ok) return;
      await new Promise((r) => setTimeout(r, 600));
    }
    setBusy('update');
    let lid = null;
    try {
      lid = notify.loading('Refreshing ROI...');
      const tx = await contract.updateROI(userId);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('ROI updated. Level income propagated upline.');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  const claim = async () => {
    if (!contract) return;
    if (!onCorrectNetwork) {
      notify.warning('Switching to correct network...');
      const ok = await switchToCorrectNetwork();
      if (!ok) return;
      await new Promise((r) => setTimeout(r, 600));
    }
    if (total.lte(0)) {
      notify.warning('No rewards to claim');
      return;
    }

    // amount = 0 → contract claims max available
    let amountArg = ethers.BigNumber.from(0);
    const effectiveAmount = amountInput ? parseUSDT(amountInput) : maxClaimable;

    if (effectiveAmount.lt(MIN_CLAIM_BN)) {
      notify.error(`Minimum claim is ${MIN_CLAIM_USDT} USDT`);
      return;
    }

    if (amountInput) {
      if (effectiveAmount.lte(0)) {
        notify.error('Enter a valid amount');
        return;
      }
      if (effectiveAmount.gt(maxClaimable)) {
        notify.error(`Amount exceeds available (${formatUSDT(maxClaimable)} USDT)`);
        return;
      }
      amountArg = effectiveAmount;
    }

    setBusy('claim');
    let lid = null;
    try {
      lid = notify.loading('Claiming rewards...');
      const tx = await contract.claimRewards(amountArg);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('Rewards claimed');
      setAmountInput('');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="claim-widget">
      <div className="claim-widget-inner">
        <div className="claim-amount-block">
          {limitEnabled && (
            <div className="claim-daily-pill">
              <span className="badge-dot" style={{ background: 'var(--accent-cyan)', boxShadow: '0 0 6px var(--accent-cyan)' }} />
              {capReached
                ? `Cap hit · Resets in ${formatTimeRemaining(secondsToReset)}`
                : `${formatUSDT(remaining)} / ${formatUSDT(dailyLimit)} USDT today`}
            </div>
          )}

          <div className="claim-amount-label">Available to Claim</div>
          <div className="claim-amount-value">
            {formatUSDT(total)} <span className="claim-amount-unit">USDT</span>
          </div>

          <div className="claim-meta-row">
            <div className="claim-meta-item">
              <span>Live ROI</span>
              <span className="text-green">{formatUSDT(pendingROI || '0')} USDT</span>
            </div>
            <div className="claim-meta-item">
              <span>Settled</span>
              <span className="text-cyan">{formatUSDT(pendingRewards || '0')} USDT</span>
            </div>
            <div className="claim-meta-item">
              <span>Fee ({bpsToPercent(claimFee || 0)}%)</span>
              <span className="text-pink">-{formatUSDT(feeBN)} USDT</span>
            </div>
            <div className="claim-meta-item">
              <span>You Receive</span>
              <span className="text-green">{formatUSDT(payout)} USDT</span>
            </div>
          </div>
        </div>

        <div className="claim-actions">
          <div className="claim-amount-input-wrap">
            <input
              className="claim-amount-input"
              type="text"
              placeholder={`Max ${formatUSDT(maxClaimable)}`}
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value.replace(/[^0-9.]/g, ''))}
              disabled={busy !== '' || maxClaimable.lte(0)}
            />
            <button
              className="claim-max-btn"
              onClick={fillMax}
              disabled={maxClaimable.lte(0)}
              type="button"
            >Max</button>
          </div>

          <Button
            variant="success"
            size="lg"
            block
            loading={busy === 'claim'}
            onClick={claim}
            disabled={total.lte(0) || busy === 'update' || maxClaimable.lt(MIN_CLAIM_BN)}
          >
            {amountInput ? 'Claim Amount' : 'Claim All'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            block
            loading={busy === 'update'}
            onClick={updateROI}
            disabled={busy === 'claim'}
          >Update ROI</Button>

          <div style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            textAlign: 'center',
            letterSpacing: '0.05em',
            marginTop: 4
          }}>
            Min claim {MIN_CLAIM_USDT} USDT · empty input = claim max
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimWidget;
